import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import * as React from 'react';

import { Button, ButtonProps } from 'bones/design-system/components/Button';

export const PAGINATION_BUTTON_PREV_TEST_ID = 'button-prev';
export const PAGINATION_BUTTON_NEXT_TEST_ID = 'button-next';
export const PAGINATION_ACTIONS_PREV_TEST_ID = 'actions-prev';
export const PAGINATION_ACTIONS_NEXT_TEST_ID = 'actions-next';

export const BUTTON_SIZE_BY_PAGINATION_SIZE: Record<
  PaginationProps['size'],
  ButtonProps['size']
> = {
  lg: 'md',
  md: 'sm',
  sm: 'xs',
};

export interface PaginationProps {
  onPage: (page: number) => void;
  page: number;
  pages: number;
  pageTestId?: (page: string | number) => string;
  size: 'lg' | 'md' | 'sm';
}

const PaginationButton: React.VFC<
  Pick<PaginationProps, 'onPage' | 'page' | 'pageTestId' | 'size'> & {
    active?: boolean;
  }
> = ({ active, onPage, page, pageTestId, size }) => (
  <Button
    active={active}
    className="vb-pagination-button"
    onClick={() => onPage(page)}
    size={BUTTON_SIZE_BY_PAGINATION_SIZE[size]}
    testId={pageTestId?.(page)}
    variant="ghost-gray"
  >
    {page}
  </Button>
);

const PaginationPlaceholder: React.VFC<Pick<PaginationProps, 'size'>> = ({
  size,
}) => (
  <Button
    className="vb-pagination-placeholder"
    size={BUTTON_SIZE_BY_PAGINATION_SIZE[size]}
    variant="ghost-gray"
  >
    ...
  </Button>
);

export const Pagination: React.VFC<PaginationProps> = ({
  onPage,
  page,
  pages,
  pageTestId,
  size,
}) => {
  let buttons: React.ReactElement;
  if (pages <= 6) {
    buttons = (
      <>
        {new Array(pages).fill('').map((_, idx) => (
          <PaginationButton
            // eslint-disable-next-line react/no-array-index-key
            key={idx + 1}
            active={idx + 1 === page}
            onPage={onPage}
            page={idx + 1}
            pageTestId={pageTestId}
            size={size}
          />
        ))}
      </>
    );
  } else if (page <= 3) {
    buttons = (
      <>
        {new Array(4).fill('').map((_, idx) => (
          <PaginationButton
            // eslint-disable-next-line react/no-array-index-key
            key={idx + 1}
            active={idx + 1 === page}
            onPage={onPage}
            page={idx + 1}
            pageTestId={pageTestId}
            size={size}
          />
        ))}
        <PaginationPlaceholder size={size} />
        <PaginationButton
          onPage={onPage}
          page={pages}
          pageTestId={pageTestId}
          size={size}
        />
      </>
    );
  } else if (page + 2 >= pages) {
    buttons = (
      <>
        <PaginationButton
          onPage={onPage}
          page={1}
          pageTestId={pageTestId}
          size={size}
        />
        <PaginationPlaceholder size={size} />
        {new Array(4).fill('').map((_, idx) => (
          <PaginationButton
            // eslint-disable-next-line react/no-array-index-key
            key={pages - 3 + idx}
            active={pages - 3 + idx === page}
            onPage={onPage}
            page={pages - 3 + idx}
            pageTestId={pageTestId}
            size={size}
          />
        ))}
      </>
    );
  } else {
    buttons = (
      <>
        <PaginationButton
          onPage={onPage}
          page={1}
          pageTestId={pageTestId}
          size={size}
        />
        <PaginationPlaceholder size={size} />
        {new Array(3).fill('').map((_, idx) => (
          <PaginationButton
            // eslint-disable-next-line react/no-array-index-key
            key={page - 1 + idx}
            active={page - 1 + idx === page}
            onPage={onPage}
            page={page - 1 + idx}
            pageTestId={pageTestId}
            size={size}
          />
        ))}
        <PaginationPlaceholder size={size} />
        <PaginationButton
          onPage={onPage}
          page={pages}
          pageTestId={pageTestId}
          size={size}
        />
      </>
    );
  }

  return (
    <div className="vb-pagination">
      {page === 1 ? null : (
        <Button
          className="vb-pagination-button"
          leftIcon={ChevronLeft}
          onClick={() => onPage(1)}
          size={BUTTON_SIZE_BY_PAGINATION_SIZE[size]}
          testId={pageTestId?.(PAGINATION_BUTTON_PREV_TEST_ID)}
          variant="ghost-gray"
        />
      )}
      {buttons}
      {page === pages ? null : (
        <Button
          className="vb-pagination-button"
          leftIcon={ChevronRight}
          onClick={() => onPage(1)}
          size={BUTTON_SIZE_BY_PAGINATION_SIZE[size]}
          testId={pageTestId?.(PAGINATION_BUTTON_NEXT_TEST_ID)}
          variant="ghost-gray"
        />
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface PaginationShowProps {
  // onShow: (show: number) => void;
  // show: number;
  // shows: number[];
}

export const PaginationShow: React.VFC<PaginationShowProps> = () => (
  <div>TODO: Show</div>
);

export const PaginationBrowse: React.VFC = () => <div>TODO: Go to Page</div>;
